import React, {useState, useEffect} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import './DetailCommercial.css';

const METHODES = [
  { value: 'virement', label: 'Virement bancaire' },
  { value: 'versement_bancaire', label: 'Versement bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'traite', label: 'Traite' },
  { value: 'especes', label: 'Espèces' },
  { value: 'carte', label: 'Carte bancaire' },
  { value: 'autre', label: 'Autre' }
];

const DetailCommercial = ({commercialId, onNavigate, currentPage}) => {
  const [commercial, setCommercial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [message, setMessage] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [clientsAssigned, setClientsAssigned] = useState([]);

  // Paiements
  const [paiements, setPaiements] = useState([]);
  const [resumePaiements, setResumePaiements] = useState({ commissionGagnee: 0, totalVerse: 0, soldeRestant: 0, pourcentagePaye: 0 });
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [editingPaiement, setEditingPaiement] = useState(null);
  const [paiementForm, setPaiementForm] = useState({ montant: '', datePaiement: '', methode: 'virement', reference: '', note: '', nomRecepteur: '' });
  const [paiementError, setPaiementError] = useState(null);
  const [paiementLoading, setPaiementLoading] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(null); // paiement dont on veut voir les logs

  const [assignFormData, setAssignFormData] = useState({
    clientId: '', dossierId: '', commissionPourcentage: '', commissionMontant: '', montantTotal: 0
  });
  const [allClients, setAllClients] = useState([]);
  const [selectedDossiers, setSelectedDossiers] = useState([]);
  const [showBulkCommissionModal, setShowBulkCommissionModal] = useState(false);
  const [bulkCommissionForm, setBulkCommissionForm] = useState({
    mode: 'unique',
    typeCommission: 'montant_fixe',
    montant: '',
    pourcentage: ''
  });
  const [bulkCommissionLoading, setBulkCommissionLoading] = useState(false);
  const [bulkCommissionError, setBulkCommissionError] = useState('');
  const [commissionConfigs, setCommissionConfigs] = useState([]);

  const [formData, setFormData] = useState({
    nom: '', prenom: '', mail: '', numeroTelephone: '', adresse: '', region: '', ville: '', age: '',
    totalSold: 0, pourcentage: 0, totalClients: 0
  });

  const [statsFormData, setStatsFormData] = useState({ totalSold: 0, pourcentage: 0, totalClients: 0 });

  const API_URL = import.meta.env.VITE_API_URL;
  const getApiUrl = (endpoint) => {
    const baseUrl = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;
    return `${baseUrl}${endpoint}`;
  };
  const getToken = () => localStorage.getItem('token');

  const showCommissionWarning = (warning) => {
    if (!warning?.message) return;
    const isNonConforming = warning.type === 'non_conforming_client_data' || warning.type === 'combined_warning';
    const title = isNonConforming ? 'Remarque d\'information' : 'Commission non configurée';
    Swal.fire({
      title: title,
      text: warning.message,
      icon: isNonConforming ? 'info' : 'warning',
      confirmButtonText: 'Compris',
      confirmButtonColor: '#1e3a5f'
    });
  };

  useEffect(() => {
    fetchCommercialDetails();
    fetchPaiements();
  }, [commercialId]);

  const fetchCommercialDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl(`/commerciaux/${commercialId}/performance`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCommercial(response.data.commercial);
      setPerformance(response.data);
      setClientsAssigned(response.data.clients || []);
      setSelectedDossiers([]);
      setFormData({
        nom: response.data.commercial.nom,
        prenom: response.data.commercial.prenom,
        mail: response.data.commercial.mail,
        numeroTelephone: response.data.commercial.numeroTelephone || '',
        adresse: response.data.commercial.adresse || '',
        region: response.data.commercial.region || '',
        ville: response.data.commercial.ville || '',
        age: response.data.commercial.age || ''
      });
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des détails du commercial');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaiements = async () => {
    try {
      const res = await axios.get(getApiUrl(`/commerciaux/${commercialId}/paiements`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPaiements(res.data.paiements || []);
      setResumePaiements(res.data.resume || { commissionGagnee: 0, totalVerse: 0, soldeRestant: 0, pourcentagePaye: 0 });
    } catch (err) {
      console.error('Erreur chargement paiements:', err);
    }
  };

  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStatsInputChange = (e) => {
    const {name, value} = e.target;
    setStatsFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(getApiUrl(`/commerciaux/${commercialId}`), formData, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setShowEditModal(false);
      fetchCommercialDetails();
      setMessage('Commercial modifié avec succès');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError('Erreur lors de la modification du commercial');
    }
  };

  const handleStatsSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(getApiUrl(`/commerciaux/${commercialId}/stats`), statsFormData, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setShowStatsModal(false);
      fetchCommercialDetails();
      setMessage('Statistiques mises à jour avec succès');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError('Erreur lors de la mise à jour des statistiques');
    }
  };

  const openEditModal = () => setShowEditModal(true);
  const openStatsModal = () => {
    setStatsFormData({ totalSold: commercial.totalSold || 0, pourcentage: commercial.pourcentage || 0, totalClients: commercial.totalClients || 0 });
    setShowStatsModal(true);
  };
  const closeModals = () => { setShowEditModal(false); setShowStatsModal(false); };

  // ── Paiements ────────────────────────────────────────────────
  const openAddPaiement = () => {
    setEditingPaiement(null);
    setPaiementForm({ montant: '', datePaiement: new Date().toISOString().split('T')[0], methode: 'virement', reference: '', note: '', nomRecepteur: '' });
    setPaiementError(null);
    setShowPaiementModal(true);
  };

  const openEditPaiement = (p) => {
    setEditingPaiement(p);
    setPaiementForm({
      montant: p.montant,
      datePaiement: new Date(p.datePaiement).toISOString().split('T')[0],
      methode: p.methode || 'virement',
      reference: p.reference || '',
      note: p.note || '',
      nomRecepteur: p.nomRecepteur || ''
    });
    setPaiementError(null);
    setShowPaiementModal(true);
  };

  const validatePaiementForm = () => {
    if (!paiementForm.montant || Number(paiementForm.montant) <= 0) return 'Le montant doit être supérieur à 0.';
    const maxAllowed = editingPaiement
      ? resumePaiements.soldeRestant + editingPaiement.montant
      : resumePaiements.soldeRestant;
    if (Number(paiementForm.montant) > maxAllowed + 0.001) {
      return `Montant trop élevé. Solde restant : ${maxAllowed.toFixed(3)} TND`;
    }
    if (!paiementForm.datePaiement) return 'La date de paiement est requise.';
    if (paiementForm.methode === 'especes' && !paiementForm.nomRecepteur?.trim()) {
      return 'Le nom du réceptionnaire est obligatoire pour un paiement en espèces.';
    }
    return null;
  };

  const handlePaiementSubmit = async (e) => {
    e.preventDefault();
    const err = validatePaiementForm();
    if (err) { setPaiementError(err); return; }
    setPaiementLoading(true);
    setPaiementError(null);
    try {
      if (editingPaiement) {
        await axios.put(getApiUrl(`/commerciaux/${commercialId}/paiements/${editingPaiement._id}`), paiementForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setMessage('Paiement modifié avec succès.');
      } else {
        await axios.post(getApiUrl(`/commerciaux/${commercialId}/paiements`), paiementForm, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setMessage('Paiement ajouté avec succès et enregistré dans l\'inventaire.');
      }
      setShowPaiementModal(false);
      await fetchPaiements();
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setPaiementError(err.response?.data?.message || 'Erreur lors de l\'enregistrement.');
    } finally {
      setPaiementLoading(false);
    }
  };

  const handleDeletePaiement = async (p) => {
    const result = await Swal.fire({
      title: 'Supprimer ce paiement ?',
      html: `<p>Montant : <strong>${formatCurrency(p.montant)}</strong></p><p style="color:#94a3b8;font-size:0.85rem">Ce paiement sera également retiré de la gestion des inventaires.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      background: '#1e293b',
      color: '#f8fafc'
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(getApiUrl(`/commerciaux/${commercialId}/paiements/${p._id}`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMessage('Paiement supprimé avec succès.');
      await fetchPaiements();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression.');
    }
  };

  const loadAllClients = async () => {
    try {
      const response = await axios.get(getApiUrl('/clients?limit=1000'), { headers: { Authorization: `Bearer ${getToken()}` } });
      setAllClients(response.data.data || []);
    } catch(err) { console.error(err); }
  };

  const openAssignForm = async (clientRow = null) => {
    if (allClients.length === 0) await loadAllClients();
    if (clientRow) {
      setAssignFormData({ clientId: clientRow._id, dossierId: clientRow.dossierId, montantTotal: clientRow.montantTotal || 0, commissionPourcentage: clientRow.commissionPourcentage || '', commissionMontant: clientRow.commissionMontant || '' });
      const formEl = document.getElementById('assign-form-section');
      if (formEl) formEl.scrollIntoView({ behavior: 'smooth' });
    } else {
      setAssignFormData({ clientId: '', dossierId: '', commissionPourcentage: '', commissionMontant: '', montantTotal: 0 });
    }
  };

  const handleAssignChange = (e) => {
    const {name, value} = e.target;
    let newForm = {...assignFormData, [name]: value};
    if (name === 'clientId') {
      const selected = allClients.find(c => c._id === value);
      if (selected && selected.dossiers && selected.dossiers.length > 0) {
        newForm.dossierId = selected.dossiers[0]._id;
        newForm.montantTotal = selected.dossiers[0].montantTotalPaiement || 0;
        newForm.commissionPourcentage = selected.dossiers[0].commissionPourcentage || '';
        newForm.commissionMontant = selected.dossiers[0].commissionMontant || '';
      } else { newForm.dossierId = ''; newForm.montantTotal = 0; }
    } else if (name === 'dossierId') {
      const selectedClient = allClients.find(c => c._id === newForm.clientId);
      if (selectedClient) {
        const dossier = selectedClient.dossiers.find(d => d._id === value);
        if (dossier) { newForm.montantTotal = dossier.montantTotalPaiement || 0; newForm.commissionPourcentage = dossier.commissionPourcentage || ''; newForm.commissionMontant = dossier.commissionMontant || ''; }
      }
    } else if (name === 'commissionPourcentage' && value !== '') {
      const valNum = parseFloat(value);
      if (!isNaN(valNum)) newForm.commissionMontant = ((valNum / 100) * newForm.montantTotal).toFixed(2);
    } else if (name === 'commissionMontant' && value !== '') {
      const valNum = parseFloat(value);
      if (!isNaN(valNum) && newForm.montantTotal > 0) newForm.commissionPourcentage = ((valNum / newForm.montantTotal) * 100).toFixed(2);
    }
    setAssignFormData(newForm);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        getApiUrl(`/commerciaux/${commercialId}/assign-client`),
        { ...assignFormData, mode: 'automatique' },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      fetchCommercialDetails();
      fetchPaiements();
      setMessage('Affectation réussie');
      showCommissionWarning(response.data?.warning);
      setTimeout(() => setMessage(null), 3000);
      setAssignFormData({ clientId: '', dossierId: '', commissionPourcentage: '', commissionMontant: '', montantTotal: 0 });
    } catch (err) { setError('Erreur lors de l\'affectation'); }
  };

  const selectionKey = (row) => `${row._id}:${row.dossierId}`;
  const selectedRows = clientsAssigned.filter(row => selectedDossiers.includes(selectionKey(row)));

  const loadCommissionConfigs = async () => {
    try {
      const response = await axios.get(getApiUrl(`/commercial-commissions?commercialId=${commercialId}&limit=100`), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCommissionConfigs(response.data.data || []);
      return response.data.data || [];
    } catch (err) {
      setBulkCommissionError('Erreur lors du chargement des configurations de commission.');
      return [];
    }
  };

  const normalizePuissance = (value) => Math.round((Number(value) || 0) * 100) / 100;
  const configForPower = (row, configs = commissionConfigs) => configs.find(config => normalizePuissance(config.puissance) === normalizePuissance(row.puissance));

  const calculateBulkPreview = (row, configs = commissionConfigs) => {
    const total = Number(row.montantTotal) || 0;
    if (bulkCommissionForm.mode === 'automatique') {
      const config = configForPower(row, configs);
      if (!config) return null;
      if (config.typeCommission === 'montant_fixe') {
        const montant = Number(config.montant) || 0;
        return {
          type: 'Montant fixe',
          commissionMontant: montant,
          commissionPourcentage: total > 0 ? (montant / total) * 100 : null
        };
      }
      const pourcentage = Number(config.pourcentage) || 0;
      return {
        type: 'Pourcentage',
        commissionMontant: (pourcentage / 100) * total,
        commissionPourcentage: pourcentage
      };
    }

    if (bulkCommissionForm.typeCommission === 'pourcentage') {
      const pourcentage = Number(bulkCommissionForm.pourcentage) || 0;
      return {
        type: 'Pourcentage',
        commissionMontant: (pourcentage / 100) * total,
        commissionPourcentage: pourcentage
      };
    }

    const montant = Number(bulkCommissionForm.montant) || 0;
    return {
      type: 'Montant fixe',
      commissionMontant: montant,
      commissionPourcentage: total > 0 ? (montant / total) * 100 : null
    };
  };

  const toggleDossierSelection = (row) => {
    const key = selectionKey(row);
    setSelectedDossiers(prev => prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]);
  };

  const toggleAllDossiersSelection = () => {
    if (selectedDossiers.length === clientsAssigned.length) {
      setSelectedDossiers([]);
    } else {
      setSelectedDossiers(clientsAssigned.map(selectionKey));
    }
  };

  const openBulkCommissionModal = async () => {
    if (selectedDossiers.length === 0) {
      setBulkCommissionError('Selectionnez au moins un dossier.');
      return;
    }
    setBulkCommissionError('');
    setBulkCommissionForm({ mode: 'unique', typeCommission: 'montant_fixe', montant: '', pourcentage: '' });
    await loadCommissionConfigs();
    setShowBulkCommissionModal(true);
  };

  const handleBulkCommissionSubmit = async (e) => {
    e.preventDefault();
    setBulkCommissionError('');

    if (selectedRows.length === 0) {
      setBulkCommissionError('Selectionnez au moins un dossier.');
      return;
    }

    if (bulkCommissionForm.mode === 'unique') {
      if (bulkCommissionForm.typeCommission === 'montant_fixe' && (!bulkCommissionForm.montant || Number(bulkCommissionForm.montant) < 0)) {
        setBulkCommissionError('Saisissez un montant fixe valide.');
        return;
      }
      if (bulkCommissionForm.typeCommission === 'pourcentage' && (!bulkCommissionForm.pourcentage || Number(bulkCommissionForm.pourcentage) < 0 || Number(bulkCommissionForm.pourcentage) > 100)) {
        setBulkCommissionError('Saisissez un pourcentage entre 0 et 100.');
        return;
      }
    }

    try {
      setBulkCommissionLoading(true);
      const response = await axios.post(getApiUrl(`/commerciaux/${commercialId}/assign-clients`), {
        dossiers: selectedRows.map(row => ({ clientId: row._id, dossierId: row.dossierId })),
        mode: bulkCommissionForm.mode,
        typeCommission: bulkCommissionForm.typeCommission,
        montant: bulkCommissionForm.montant,
        pourcentage: bulkCommissionForm.pourcentage
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      setShowBulkCommissionModal(false);
      setSelectedDossiers([]);
      setMessage('Commissions affectees avec succes');
      showCommissionWarning(response.data?.warning);
      await fetchCommercialDetails();
      await fetchPaiements();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      const missing = err.response?.data?.missingConfigurations || [];
      setBulkCommissionError(missing.length > 0
        ? `Commission manquante pour: ${missing.map(item => `${item.puissance} kWc`).join(', ')}`
        : (err.response?.data?.message || 'Erreur lors de l affectation des commissions'));
    } finally {
      setBulkCommissionLoading(false);
    }
  };

  const formatCurrency = (value) => new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(value || 0);
  const formatKWc = (value) => Math.round((Number(value) || 0) * 10) / 10;
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
  const formatDateTime = (dateString) => new Date(dateString).toLocaleString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const methodeLabel = (m) => METHODES.find(x => x.value === m)?.label || m;

  const chartColors = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6'];
  const donutData = React.useMemo(() => {
    const dataMap = {};
    let total = 0;
    clientsAssigned.forEach(c => {
      const status = c.etatDossier || 'En attente';
      dataMap[status] = (dataMap[status] || 0) + (c.montantTotal || 0);
      total += (c.montantTotal || 0);
    });
    if (total === 0) return [{ name: 'Aucune donnée', value: 1 }];
    return Object.keys(dataMap).map(key => ({ name: key, value: dataMap[key] }));
  }, [clientsAssigned]);

  if (loading) return (
    <div className="dc-layout">
      <BackOfficeMenu onNavigate={onNavigate} currentPage={currentPage} />
      <div className="dc-content-wrapper"><LoadingSpinner message="Chargement des détails du commercial..." /></div>
    </div>
  );

  if (error || !commercial) return (
    <div className="dc-layout">
      <BackOfficeMenu onNavigate={onNavigate} currentPage={currentPage} />
      <div className="dc-content-wrapper">
        <div className="error-message">{error || 'Commercial non trouvé'}</div>
        <button className="btn-back" onClick={() => onNavigate('commerciaux')}>Retour à la liste</button>
      </div>
    </div>
  );

  const commissionGagnee = performance?.commissionGagnee || 0;
  const totalVentesFormat = formatCurrency(performance?.totalVentes || 0);
  const { totalVerse, soldeRestant, pourcentagePaye } = resumePaiements;

  const barData = [
    { name: 'Jan', ventes: 2000000 }, { name: 'Fév', ventes: 3500000 }, { name: 'Mar', ventes: 2500000 },
    { name: 'Avr', ventes: 6500000 }, { name: 'Mai', ventes: 4200000 }, { name: 'Juin', ventes: 3000000 }
  ];

  return (
    <div className="dc-layout">
      <BackOfficeMenu onNavigate={onNavigate} currentPage={currentPage} />
      <div className="dc-content-wrapper">

        {/* Breadcrumb */}
        <div className="dc-top-nav">
          <div className="dc-breadcrumbs">
            <span onClick={() => onNavigate('commerciaux')} style={{ cursor: 'pointer', color: '#94a3b8' }}>Commerciaux</span>
            <i className="fas fa-chevron-right" style={{ margin: '0 8px', fontSize: '0.7rem', color: '#64748b' }}></i>
            <span className="current">Profil Commercial</span>
          </div>
        </div>

        {message && (
          <div className="success-message-detail"><i className="fas fa-check-circle"></i> {message}</div>
        )}
        {bulkCommissionError && !showBulkCommissionModal && (
          <div className="dc-inline-warning"><i className="fas fa-exclamation-triangle"></i> {bulkCommissionError}</div>
        )}
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {/* Profile Header */}
        <div className="dc-profile-header">
          <div className="dc-profile-info">
            <div className="dc-profile-title">
              <h1>{commercial.prenom} {commercial.nom}</h1>
              <span className="dc-status-badge">Actif</span>
            </div>
            <div className="dc-profile-meta">
              <span><i className="fas fa-briefcase"></i> Commercial</span>
              <span><i className="fas fa-envelope"></i> {commercial.mail}</span>
              {commercial.numeroTelephone && <span><i className="fas fa-phone"></i> {commercial.numeroTelephone}</span>}
            </div>
          </div>
          <div className="dc-profile-actions">
            <button className="dc-btn-outline" onClick={() => onNavigate('commerciaux')}>
              <i className="fas fa-arrow-left"></i> Retour
            </button>
            <button className="dc-btn-primary" onClick={openEditModal}>Modifier</button>
            <button className="dc-btn-outline" onClick={() => onNavigate('commercial-commissions')}>
              <i className="fas fa-sliders-h"></i> Commissions
            </button>
            <button className="dc-btn-outline" onClick={openStatsModal}>
              <i className="fas fa-chart-line"></i> Statistiques
            </button>
          </div>
        </div>

        {/* Stats Row — 6 cartes */}
        <div className="dc-stats-row dc-stats-row-6">
          <div className="dc-stat-card">
            <div className="dc-stat-header">
              <div className="dc-stat-icon blue"><i className="fas fa-chart-bar"></i></div>
              <span>Total des ventes</span>
            </div>
            <h3 className="dc-stat-value">{totalVentesFormat}</h3>
          </div>
          <div className="dc-stat-card">
            <div className="dc-stat-header">
              <div className="dc-stat-icon blue"><i className="fas fa-bolt"></i></div>
              <span>Total kWc</span>
            </div>
            <h3 className="dc-stat-value">{formatKWc(performance?.totalKWc)} kWc</h3>
          </div>
          <div className="dc-stat-card">
            <div className="dc-stat-header">
              <div className="dc-stat-icon gray"><i className="fas fa-users"></i></div>
              <span>Clients</span>
            </div>
            <h3 className="dc-stat-value">{performance?.totalClients || 0}</h3>
          </div>
          <div className="dc-stat-card">
            <div className="dc-stat-header">
              <div className="dc-stat-icon yellow"><i className="fas fa-trophy"></i></div>
              <span>Commission gagnée</span>
            </div>
            <h3 className="dc-stat-value">{formatCurrency(commissionGagnee)}</h3>
          </div>
          <div className="dc-stat-card">
            <div className="dc-stat-header">
              <div className="dc-stat-icon" style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
                <i className="fas fa-hand-holding-usd"></i>
              </div>
              <span>Total versé</span>
            </div>
            <h3 className="dc-stat-value" style={{ color: '#10b981' }}>{formatCurrency(totalVerse)}</h3>
          </div>
          <div className="dc-stat-card">
            <div className="dc-stat-header">
              <div className="dc-stat-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
                <i className="fas fa-balance-scale"></i>
              </div>
              <span>Solde restant</span>
            </div>
            <h3 className="dc-stat-value" style={{ color: soldeRestant > 0 ? '#f59e0b' : '#10b981' }}>
              {formatCurrency(soldeRestant)}
            </h3>
            {commissionGagnee > 0 && (
              <>
                <div className="dc-commission-progress">
                  <div className="dc-commission-bar" style={{ width: `${Math.min(100, pourcentagePaye)}%` }}></div>
                </div>
                <span className="dc-commission-pct">{pourcentagePaye}% versé</span>
              </>
            )}
          </div>
        </div>

        {/* Info Grid */}
        <div className="dc-info-grid">
          <div className="dc-info-card">
            <h3>Informations personnelles</h3>
            <div className="dc-info-row"><span className="dc-info-label"><i className="fas fa-user"></i> Nom complet</span><span className="dc-info-value">{commercial.prenom} {commercial.nom}</span></div>
            <div className="dc-info-row"><span className="dc-info-label"><i className="fas fa-envelope"></i> Email</span><span className="dc-info-value">{commercial.mail}</span></div>
            <div className="dc-info-row"><span className="dc-info-label"><i className="fas fa-phone"></i> Téléphone</span><span className="dc-info-value">{commercial.numeroTelephone || 'Non renseigné'}</span></div>
            <div className="dc-info-row"><span className="dc-info-label"><i className="fas fa-clock"></i> Âge</span><span className="dc-info-value">{commercial.age ? `${commercial.age} ans` : 'Non renseigné'}</span></div>
          </div>
          <div className="dc-info-card">
            <h3>Localisation</h3>
            <div className="dc-info-row"><span className="dc-info-label">Ville</span><span className="dc-info-value">{commercial.ville || 'Non renseignée'}</span></div>
            <div className="dc-info-row"><span className="dc-info-label">Région</span><span className="dc-info-value">{commercial.region || 'Non renseignée'}</span></div>
            <div className="dc-info-row"><span className="dc-info-label">Adresse</span><span className="dc-info-value">{commercial.adresse || 'Non renseignée'}</span></div>
          </div>
          <div className="dc-info-card">
            <h3>Performance commerciale</h3>
            <div className="dc-info-row"><span className="dc-info-label">Total des ventes</span><span className="dc-info-value">{totalVentesFormat}</span></div>
            <div className="dc-info-row"><span className="dc-info-label">Nombre de clients</span><span className="dc-info-value">{performance?.totalClients || 0}</span></div>
            <div className="dc-info-row"><span className="dc-info-label">Commission totale</span><span className="dc-info-value highlight">{formatCurrency(commissionGagnee)}</span></div>
            <div className="dc-info-row"><span className="dc-info-label">Vente moyenne / client</span><span className="dc-info-value">{performance?.totalClients ? formatCurrency((performance?.totalVentes || 0) / performance.totalClients) : '0,000 DT'}</span></div>
          </div>
          <div className="dc-info-card">
            <h3>Informations système</h3>
            <div className="dc-info-row"><span className="dc-info-label">ID</span><span className="dc-info-value" style={{fontSize: '11px'}}>{commercial._id}</span></div>
            <div className="dc-info-row"><span className="dc-info-label">Type</span><span className="dc-info-value">{commercial.typeUser || 'commercial'}</span></div>
            <div className="dc-info-row"><span className="dc-info-label">Création</span><span className="dc-info-value">{formatDateTime(commercial.createdAt)}</span></div>
            <div className="dc-info-row"><span className="dc-info-label">Dernière modif.</span><span className="dc-info-value">{formatDateTime(commercial.updatedAt)}</span></div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════
            SECTION HISTORIQUE DES PAIEMENTS
        ══════════════════════════════════════════════════ */}
        <div className="dc-paiements-section">
          <div className="dc-paiements-header">
            <div>
              <h3 className="dc-section-title">
                <i className="fas fa-money-bill-wave" style={{ color: '#ffd700', marginRight: '10px' }}></i>
                Historique des Paiements
              </h3>
              <p className="dc-section-subtitle">
                Commission gagnée : <strong style={{ color: '#ffd700' }}>{formatCurrency(commissionGagnee)}</strong>
                &nbsp;·&nbsp; Versé : <strong style={{ color: '#10b981' }}>{formatCurrency(totalVerse)}</strong>
                &nbsp;·&nbsp; Restant : <strong style={{ color: soldeRestant > 0 ? '#f59e0b' : '#10b981' }}>{formatCurrency(soldeRestant)}</strong>
              </p>
            </div>
            <button
              className="dc-btn-paiement-add"
              onClick={openAddPaiement}
              disabled={soldeRestant <= 0}
              title={soldeRestant <= 0 ? 'Commission totalement versée' : 'Ajouter un paiement'}
            >
              <i className="fas fa-plus"></i> Nouveau Paiement
            </button>
          </div>

          {/* Barre de progression globale */}
          {commissionGagnee > 0 && (
            <div className="dc-progress-bar-wrapper">
              <div className="dc-progress-bar-track">
                <div
                  className="dc-progress-bar-fill"
                  style={{ width: `${Math.min(100, pourcentagePaye)}%` }}
                ></div>
              </div>
              <span className="dc-progress-label">{pourcentagePaye}% de la commission versée</span>
            </div>
          )}

          {/* Tableau des paiements */}
          <div className="dc-paiements-table-wrapper">
            <table className="dc-paiements-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Montant</th>
                  <th>Méthode</th>
                  <th>Référence</th>
                  <th>Note</th>
                  <th>Saisi par</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paiements.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', color: '#64748b', padding: '32px' }}>
                      <i className="fas fa-inbox" style={{ fontSize: '2rem', display: 'block', marginBottom: '8px', opacity: 0.4 }}></i>
                      Aucun paiement enregistré pour ce commercial.
                    </td>
                  </tr>
                ) : (
                  paiements.map((p) => (
                    <tr key={p._id}>
                      <td>
                        <span className="dc-paiement-date">{formatDate(p.datePaiement)}</span>
                      </td>
                      <td>
                        <span className="dc-paiement-montant">{formatCurrency(p.montant)}</span>
                      </td>
                      <td>
                        <span className={`dc-methode-badge dc-methode-badge--${p.methode}`}>
                          {methodeLabel(p.methode)}
                        </span>
                        {p.methode === 'especes' && p.nomRecepteur && (
                          <div style={{ fontSize: '0.78rem', color: '#ffd700', marginTop: '3px' }}>
                            <i className="fas fa-user-check" style={{ marginRight: '4px' }}></i>
                            {p.nomRecepteur}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{p.reference || '—'}</span>
                      </td>
                      <td>
                        <span style={{ color: '#cbd5e1', fontSize: '0.85rem', maxWidth: '150px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.note || '—'}
                        </span>
                      </td>
                      <td>
                        <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                          {p.createdBy ? `${p.createdBy.prenom} ${p.createdBy.nom}` : '—'}
                        </span>
                      </td>
                      <td>
                        <div className="dc-paiement-actions">
                          <button
                            className="dc-paiement-btn dc-paiement-btn--logs"
                            onClick={() => setShowLogsModal(p)}
                            title="Voir les logs"
                          >
                            <i className="fas fa-history"></i>
                          </button>
                          <button
                            className="dc-paiement-btn dc-paiement-btn--edit"
                            onClick={() => openEditPaiement(p)}
                            title="Modifier"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="dc-paiement-btn dc-paiement-btn--delete"
                            onClick={() => handleDeletePaiement(p)}
                            title="Supprimer"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Assign Section */}
        <div id="assign-form-section" className="dc-assign-section">
          <div className="dc-assign-header">
            <h3>Affectation / Paramétrage de commission</h3>
            <button className="dc-btn-yellow" onClick={() => openAssignForm()}>
              <i className="fas fa-plus"></i> Nouvelle affectation
            </button>
          </div>
          <form onSubmit={handleAssignSubmit} className="dc-assign-form">
            <div className="dc-form-group dc-assign-client">
              <label>Sélectionner le client</label>
              <select className="dc-form-control" name="clientId" value={assignFormData.clientId} onChange={handleAssignChange} onClick={allClients.length === 0 ? loadAllClients : undefined} required>
                <option value="">Choisir un client</option>
                {allClients.map(c => (<option key={c._id} value={c._id}>{c.prenom} {c.nom}</option>))}
              </select>
            </div>
            <div className="dc-form-group dc-assign-dossier">
              <label>Dossier</label>
              <select className="dc-form-control" name="dossierId" value={assignFormData.dossierId} onChange={handleAssignChange} required disabled={!assignFormData.clientId}>
                {!assignFormData.clientId ? <option value="">Choisir un dossier</option> : (
                  allClients.find(c => c._id === assignFormData.clientId)?.dossiers?.map(d => (
                    <option key={d._id} value={d._id}>{d.referenceSteg || 'N/A'} - {d.puissance}kWc</option>
                  ))
                )}
              </select>
            </div>
            <div className="dc-form-group dc-assign-montant-total">
              <label>Montant total</label>
              <input type="text" className="dc-form-control" value={formatCurrency(assignFormData.montantTotal || 0)} disabled />
            </div>
            <div className="dc-form-group dc-assign-pourcentage">
              <label>Comm. (%)</label>
              <input type="number" className="dc-form-control" name="commissionPourcentage" value={assignFormData.commissionPourcentage} onChange={handleAssignChange} step="0.01" min="0" max="100" placeholder="Ex: 5" />
            </div>
            <div className="dc-form-group dc-assign-montant">
              <label>Montant (TND)</label>
              <input type="number" className="dc-form-control" name="commissionMontant" value={assignFormData.commissionMontant} onChange={handleAssignChange} step="0.01" min="0" placeholder="Ex: 1500" />
            </div>
            <div className="dc-assign-actions">
              <button type="submit" className="dc-btn-primary" style={{height: '40px', padding: '0 24px'}}>Valider</button>
            </div>
          </form>
        </div>

        {/* Bottom Grid */}
        <div className="dc-bottom-grid">
          <div className="dc-charts-card">
            <h3 className="dc-card-title">Aperçu des performances</h3>
            <div className="dc-charts-container">
              <div className="dc-chart-donut">
                <div className="dc-chart-title">Répartition des ventes</div>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                      {donutData.map((entry, index) => (<Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />))}
                    </Pie>
                    <RechartsTooltip formatter={(val) => formatCurrency(val)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="dc-chart-bar">
                <div className="dc-chart-title">Ventes par mois (DT)</div>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b'}} />
                    <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b'}} tickFormatter={(v) => `${v/1000000}M`} />
                    <RechartsTooltip cursor={{fill: 'transparent'}} formatter={(val) => formatCurrency(val)} />
                    <Bar dataKey="ventes" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="dc-table-card">
            <div className="dc-clients-card-header">
              <div>
                <h3 className="dc-card-title">Clients affectés</h3>
                <span className="dc-selection-count">{selectedRows.length} dossier(s) selectionne(s)</span>
              </div>
              <button className="dc-btn-primary dc-bulk-commission-btn" onClick={openBulkCommissionModal} disabled={selectedRows.length === 0}>
                <i className="fas fa-coins"></i> Affecter une commission
              </button>
            </div>
            <table className="dc-clients-table">
              <thead>
                <tr>
                  <th className="dc-check-cell">
                    <input
                      type="checkbox"
                      checked={clientsAssigned.length > 0 && selectedRows.length === clientsAssigned.length}
                      onChange={toggleAllDossiersSelection}
                      aria-label="Selectionner tous les dossiers"
                    />
                  </th>
                  <th>Client</th><th>Puissance</th><th>Montant total</th><th>Commission</th><th>Statut</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clientsAssigned.length === 0 ? (
                  <tr><td colSpan="7" style={{textAlign: 'center', color: '#64748b'}}>Aucun client affecté.</td></tr>
                ) : (
                  clientsAssigned.map((c, i) => {
                    const isSuccess = c.etatDossier === 'Réceptionné' || c.etatDossier === 'Installé';
                    const isWarning = c.etatDossier === 'En attente';
                    const isDanger = c.etatDossier === 'Annulé';
                    const statusClass = isSuccess ? 'success' : isWarning ? 'warning' : isDanger ? 'danger' : '';
                    return (
                      <tr key={i} className={selectedDossiers.includes(selectionKey(c)) ? 'dc-row-selected' : ''}>
                        <td className="dc-check-cell">
                          <input
                            type="checkbox"
                            checked={selectedDossiers.includes(selectionKey(c))}
                            onChange={() => toggleDossierSelection(c)}
                            aria-label={`Selectionner ${c.prenom} ${c.nom}`}
                          />
                        </td>
                        <td className="dc-client-name">{c.prenom} {c.nom}</td>
                        <td>{c.puissance} kWc</td>
                        <td>{formatCurrency(c.montantTotal)}</td>
                        <td>
                          {c.commissionMontant || c.commissionPourcentage ? (
                            <span style={{color: '#0f172a'}}>{formatCurrency(c.commissionMontant)}</span>
                          ) : (
                            <span className="dc-comm-pending">À définir</span>
                          )}
                        </td>
                        <td className={`dc-status-cell ${statusClass}`}>{c.etatDossier || 'En cours'}</td>
                        <td>
                          <button className="dc-btn-icon" onClick={() => openAssignForm(c)} title="Définir commission">
                            <i className="fas fa-edit"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showBulkCommissionModal && (
          <div className="dc-modal-overlay" onClick={() => setShowBulkCommissionModal(false)}>
            <div className="dc-modal-paiement dc-bulk-modal" onClick={e => e.stopPropagation()}>
              <div className="dc-modal-paiement-header">
                <h2>
                  <i className="fas fa-coins" style={{ color: '#ffd700', marginRight: '10px' }}></i>
                  Affecter une commission
                </h2>
                <button className="dc-modal-close" onClick={() => setShowBulkCommissionModal(false)}>&times;</button>
              </div>

              <form onSubmit={handleBulkCommissionSubmit} className="dc-paiement-form">
                <div className="dc-mode-selector">
                  <label className={bulkCommissionForm.mode === 'unique' ? 'active' : ''}>
                    <input
                      type="radio"
                      name="mode"
                      value="unique"
                      checked={bulkCommissionForm.mode === 'unique'}
                      onChange={e => setBulkCommissionForm(prev => ({ ...prev, mode: e.target.value }))}
                    />
                    <span>Commission unique</span>
                  </label>
                  <label className={bulkCommissionForm.mode === 'automatique' ? 'active' : ''}>
                    <input
                      type="radio"
                      name="mode"
                      value="automatique"
                      checked={bulkCommissionForm.mode === 'automatique'}
                      onChange={e => setBulkCommissionForm(prev => ({ ...prev, mode: e.target.value }))}
                    />
                    <span>Automatique par puissance</span>
                  </label>
                </div>

                {bulkCommissionForm.mode === 'unique' && (
                  <div className="dc-form-row-2">
                    <div className="dc-form-group-modal">
                      <label>Type de commission</label>
                      <select
                        value={bulkCommissionForm.typeCommission}
                        onChange={e => setBulkCommissionForm(prev => ({ ...prev, typeCommission: e.target.value }))}
                      >
                        <option value="montant_fixe">Montant fixe</option>
                        <option value="pourcentage">Pourcentage</option>
                      </select>
                    </div>
                    {bulkCommissionForm.typeCommission === 'montant_fixe' ? (
                      <div className="dc-form-group-modal">
                        <label>Montant (TND)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={bulkCommissionForm.montant}
                          onChange={e => setBulkCommissionForm(prev => ({ ...prev, montant: e.target.value }))}
                          placeholder="Ex: 1500"
                        />
                      </div>
                    ) : (
                      <div className="dc-form-group-modal">
                        <label>Pourcentage (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={bulkCommissionForm.pourcentage}
                          onChange={e => setBulkCommissionForm(prev => ({ ...prev, pourcentage: e.target.value }))}
                          placeholder="Ex: 5"
                        />
                      </div>
                    )}
                  </div>
                )}

                {bulkCommissionForm.mode === 'automatique' && (
                  <div className="dc-auto-info">
                    Le systeme applique la grille configuree pour chaque puissance du commercial.
                  </div>
                )}

                <div className="dc-bulk-preview">
                  <div className="dc-bulk-preview-title">
                    <span>{selectedRows.length} dossier(s) selectionne(s)</span>
                    <button type="button" onClick={loadCommissionConfigs}>
                      <i className="fas fa-sync-alt"></i> Actualiser
                    </button>
                  </div>
                  <div className="dc-bulk-preview-list">
                    {selectedRows.map(row => {
                      const preview = calculateBulkPreview(row);
                      return (
                        <div className={`dc-bulk-preview-row ${!preview ? 'missing' : ''}`} key={selectionKey(row)}>
                          <div>
                            <strong>{row.prenom} {row.nom}</strong>
                            <span>{row.puissance} kWc - {formatCurrency(row.montantTotal)}</span>
                          </div>
                          {preview ? (
                            <div className="dc-bulk-preview-amount">
                              <strong>{formatCurrency(preview.commissionMontant)}</strong>
                              <span>{preview.commissionPourcentage !== null ? `${preview.commissionPourcentage.toFixed(2)}%` : preview.type}</span>
                            </div>
                          ) : (
                            <div className="dc-bulk-missing">Config. manquante</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {bulkCommissionError && (
                  <div className="dc-paiement-error">
                    <i className="fas fa-exclamation-triangle"></i> {bulkCommissionError}
                  </div>
                )}

                <div className="dc-modal-actions-paiement">
                  <button type="button" className="dc-btn-cancel-modal" onClick={() => setShowBulkCommissionModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="dc-btn-save-paiement" disabled={bulkCommissionLoading}>
                    {bulkCommissionLoading ? <><i className="fas fa-spinner fa-spin"></i> Validation...</> : 'Valider'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ══════════════════ MODAL PAIEMENT ══════════════════ */}
        {showPaiementModal && (
          <div className="dc-modal-overlay" onClick={() => setShowPaiementModal(false)}>
            <div className="dc-modal-paiement" onClick={e => e.stopPropagation()}>
              <div className="dc-modal-paiement-header">
                <h2>
                  <i className="fas fa-money-bill-wave" style={{ color: '#ffd700', marginRight: '10px' }}></i>
                  {editingPaiement ? 'Modifier le paiement' : 'Nouveau paiement'}
                </h2>
                <button className="dc-modal-close" onClick={() => setShowPaiementModal(false)}>&times;</button>
              </div>

              {/* Résumé plafond */}
              <div className="dc-paiement-plafond-info">
                <div className="dc-plafond-row">
                  <span>Commission gagnée</span>
                  <strong style={{ color: '#ffd700' }}>{formatCurrency(resumePaiements.commissionGagnee)}</strong>
                </div>
                <div className="dc-plafond-row">
                  <span>Déjà versé {editingPaiement ? '(hors ce paiement)' : ''}</span>
                  <strong style={{ color: '#10b981' }}>{formatCurrency(editingPaiement ? (totalVerse - editingPaiement.montant) : totalVerse)}</strong>
                </div>
                <div className="dc-plafond-row dc-plafond-row--max">
                  <span>Montant max autorisé</span>
                  <strong style={{ color: '#f59e0b' }}>
                    {formatCurrency(editingPaiement ? (resumePaiements.soldeRestant + editingPaiement.montant) : resumePaiements.soldeRestant)}
                  </strong>
                </div>
              </div>

              <form onSubmit={handlePaiementSubmit} className="dc-paiement-form">
                <div className="dc-form-row-2">
                  <div className="dc-form-group-modal">
                    <label>Montant (TND) *</label>
                    <input
                      type="number"
                      step="0.001"
                      min="0.001"
                      value={paiementForm.montant}
                      onChange={e => setPaiementForm(p => ({ ...p, montant: e.target.value }))}
                      placeholder="Ex: 500.000"
                      required
                    />
                  </div>
                  <div className="dc-form-group-modal">
                    <label>Date de paiement *</label>
                    <input
                      type="date"
                      value={paiementForm.datePaiement}
                      onChange={e => setPaiementForm(p => ({ ...p, datePaiement: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="dc-form-row-2">
                  <div className="dc-form-group-modal">
                    <label>Méthode de paiement</label>
                    <select
                      value={paiementForm.methode}
                      onChange={e => {
                        const newMethode = e.target.value;
                        setPaiementForm(p => ({
                          ...p,
                          methode: newMethode,
                          nomRecepteur: newMethode === 'especes' ? p.nomRecepteur : ''
                        }));
                      }}
                    >
                      {METHODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div className="dc-form-group-modal">
                    <label>Référence (optionnel)</label>
                    <input
                      type="text"
                      value={paiementForm.reference}
                      onChange={e => setPaiementForm(p => ({ ...p, reference: e.target.value }))}
                      placeholder="N° chèque, virement..."
                    />
                  </div>
                </div>

                {paiementForm.methode === 'especes' && (
                  <div className="dc-form-group-modal" style={{ width: '100%', marginBottom: '16px' }}>
                    <label>Nom du réceptionnaire *</label>
                    <input
                      type="text"
                      value={paiementForm.nomRecepteur}
                      onChange={e => setPaiementForm(p => ({ ...p, nomRecepteur: e.target.value }))}
                      placeholder="Nom et prénom de la personne ayant reçu l'espèces"
                      required
                    />
                  </div>
                )}

                <div className="dc-form-group-modal" style={{ width: '100%' }}>
                  <label>Note (optionnel)</label>
                  <textarea
                    value={paiementForm.note}
                    onChange={e => setPaiementForm(p => ({ ...p, note: e.target.value }))}
                    placeholder="Informations supplémentaires..."
                    rows={2}
                  />
                </div>

                {paiementError && (
                  <div className="dc-paiement-error">
                    <i className="fas fa-exclamation-triangle"></i> {paiementError}
                  </div>
                )}

                <div className="dc-modal-actions-paiement">
                  <button type="button" className="dc-btn-cancel-modal" onClick={() => setShowPaiementModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="dc-btn-save-paiement" disabled={paiementLoading}>
                    {paiementLoading ? <><i className="fas fa-spinner fa-spin"></i> Enregistrement...</> : (editingPaiement ? 'Modifier' : 'Enregistrer')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ══════════════════ MODAL LOGS ══════════════════ */}
        {showLogsModal && (
          <div className="dc-modal-overlay" onClick={() => setShowLogsModal(null)}>
            <div className="dc-modal-logs" onClick={e => e.stopPropagation()}>
              <div className="dc-modal-paiement-header">
                <h2>
                  <i className="fas fa-history" style={{ color: '#3b82f6', marginRight: '10px' }}></i>
                  Historique — {formatCurrency(showLogsModal.montant)}
                </h2>
                <button className="dc-modal-close" onClick={() => setShowLogsModal(null)}>&times;</button>
              </div>
              <div className="dc-logs-timeline">
                {(showLogsModal.historique || []).length === 0 ? (
                  <p style={{ color: '#64748b', textAlign: 'center', padding: '24px' }}>Aucun log disponible.</p>
                ) : (
                  [...showLogsModal.historique].reverse().map((log, i) => (
                    <div key={i} className={`dc-log-item dc-log-item--${log.action}`}>
                      <div className="dc-log-icon">
                        {log.action === 'creation' ? '✅' : log.action === 'modification' ? '✏️' : '🗑️'}
                      </div>
                      <div className="dc-log-content">
                        <div className="dc-log-action">
                          <strong>{log.action === 'creation' ? 'Création' : log.action === 'modification' ? 'Modification' : 'Suppression'}</strong>
                          <span className="dc-log-date">{formatDateTime(log.date)}</span>
                        </div>
                        {log.note && <p className="dc-log-note">{log.note}</p>}
                        {log.ancienMontant !== null && log.ancienMontant !== undefined && (
                          <p className="dc-log-amounts">
                            <span style={{ color: '#ef4444' }}>{formatCurrency(log.ancienMontant)}</span>
                            <span style={{ margin: '0 8px', color: '#64748b' }}>→</span>
                            <span style={{ color: '#10b981' }}>{formatCurrency(log.nouveauMontant)}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal-overlay" onClick={closeModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Modifier le commercial</h2>
                <button className="modal-close" onClick={closeModals}>&times;</button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div style={{display: 'flex', gap: '15px'}}>
                    <div className="form-group" style={{flex: 1}}><label>Prénom *</label><input type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} required /></div>
                    <div className="form-group" style={{flex: 1}}><label>Nom *</label><input type="text" name="nom" value={formData.nom} onChange={handleInputChange} required /></div>
                  </div>
                  <div className="form-group"><label>Email *</label><input type="email" name="mail" value={formData.mail} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Téléphone</label><input type="text" name="numeroTelephone" value={formData.numeroTelephone} onChange={handleInputChange} /></div>
                  <div className="modal-actions">
                    <button type="button" className="btn-cancel" onClick={closeModals}>Annuler</button>
                    <button type="submit" className="btn-save">Enregistrer</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Stats Modal */}
        {showStatsModal && (
          <div className="modal-overlay" onClick={closeModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Statistiques</h2>
                <button className="modal-close" onClick={closeModals}>&times;</button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleStatsSubmit}>
                  <div className="form-group"><label>Total Ventes (TND)</label><input type="number" name="totalSold" value={statsFormData.totalSold} onChange={handleStatsInputChange} min="0" step="0.01" required /></div>
                  <div className="form-group"><label>Pourcentage Commission (%)</label><input type="number" name="pourcentage" value={statsFormData.pourcentage} onChange={handleStatsInputChange} min="0" max="100" step="0.01" required /></div>
                  <div className="form-group"><label>Nombre Total de Clients</label><input type="number" name="totalClients" value={statsFormData.totalClients} onChange={handleStatsInputChange} min="0" required /></div>
                  <div className="modal-actions">
                    <button type="button" className="btn-cancel" onClick={closeModals}>Annuler</button>
                    <button type="submit" className="btn-save">Mettre à jour</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DetailCommercial;
